"use client";

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Home, MapPin, Star, ChevronLeft, ChevronRight } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import type { SimilarProperty } from "@/lib/data/property-review-data";

interface SimilarPropertiesProps {
  properties: SimilarProperty[];
  currentCountry: string;
  currentType: string;
}

// Helper function to build review detail page URL
function buildReviewDetailUrl(property: SimilarProperty, country: string, type: string): string {
  // Format: /listings/reviews/[country]/[type]/[state]/[city]/[public_id]/[slug]
  const slug = property.property_slug || property.generated_title?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'property-review';
  
  return `/listings/reviews/${country}/${type}/${property.state.toLowerCase()}/${property.city.toLowerCase()}/${property.public_id}/${slug}`;
}

// Helper function to format price with currency
function formatPrice(price: number, currency: string): string {
  if (price >= 1000000) {
    return `${currency} ${(price / 1000000).toFixed(1)}M`;
  } else if (price >= 1000) {
    return `${currency} ${(price / 1000).toFixed(1)}K`;
  } else {
    return `${currency} ${price.toLocaleString()}`;
  }
}

// Helper function to calculate overall rating
function calculateOverallRating(summaryRatings: Record<string, number>): number {
  const ratings = Object.values(summaryRatings).filter(rating => typeof rating === 'number' && rating > 0);
  if (ratings.length === 0) return 0;
  
  const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
  return Math.round(average * 10) / 10;
}

const SimilarProperties = ({ properties, currentCountry, currentType }: SimilarPropertiesProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Limit to maximum 6 properties
  const limitedProperties = properties.slice(0, 6);
  
  // Early return for empty properties
  if (limitedProperties.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Home className="h-5 w-5 text-gray-600" />
          Similar Properties Nearby
        </h3>
        <div className="text-center py-8">
          <div className="p-4 rounded-full bg-gray-100 w-fit mx-auto mb-4">
            <Home className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-500">
            No similar properties found in this area.
          </p>
        </div>
      </div>
    );
  }
  
  // Mobile: Show one property at a time, Desktop: Show 2-3 properties
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const itemsPerView = isMobile ? 1 : Math.min(2, limitedProperties.length);
  const maxIndex = Math.max(0, limitedProperties.length - itemsPerView);
  
  const handlePrevious = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };
  
  const handleNext = () => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };
  
  const visibleProperties = limitedProperties.slice(currentIndex, currentIndex + itemsPerView);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Home className="h-5 w-5 text-gray-600" />
          Similar Properties Nearby
        </h3>
        
        {/* Carousel Controls */}
        {limitedProperties.length > itemsPerView && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"  
              size="sm"
              onClick={handleNext}
              disabled={currentIndex >= maxIndex}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      
      {/* Properties Grid/Carousel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {visibleProperties.map((property) => {
          const overallRating = calculateOverallRating(property.summary_ratings);
          const reviewUrl = buildReviewDetailUrl(property, currentCountry, currentType);
          
          // Helper function to get image source with fallback
          const getImageSrc = () => {
            if (!property.featured_image) {
              return "/placeholder.svg";
            }
            
            // If already a full URL, return as-is
            if (property.featured_image.startsWith('http://') || property.featured_image.startsWith('https://')) {
              return property.featured_image;
            }
            
            // Construct Supabase storage URL
            const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
            if (supabaseUrl) {
              return `${supabaseUrl}/storage/v1/object/public/reviews-media/${property.featured_image}`;
            }
            
            return "/placeholder.svg";
          };
          
          const isPlaceholderImage = !property.featured_image;

          return (
            <Card key={property.public_id} className="overflow-hidden border border-gray-200 hover:shadow-md transition-all duration-300">
              <div className="relative h-40 sm:h-48 w-full overflow-hidden bg-gray-100">
                {/* Property Image with Next.js Image optimization */}
                <Image 
                  src={getImageSrc()}
                  alt={`Property: ${property.generated_title || `Property in ${property.city}`}`}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  className="object-cover transition-transform duration-300 ease-out hover:scale-105"
                  priority={false}
                  quality={85}
                  placeholder={isPlaceholderImage ? undefined : "blur"}
                  blurDataURL={isPlaceholderImage ? undefined : "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="}
                  onError={(e) => {
                    // Fallback to placeholder on error
                    const target = e.target as HTMLImageElement;
                    target.src = "/placeholder.svg";
                  }}
                />
                
                {/* Fallback for when no image is available */}
                {isPlaceholderImage && (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
                    <Home className="h-12 w-12 text-blue-300" />
                  </div>
                )}
                
                {/* Rating Badge */}
                {overallRating > 0 && (
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-md px-2 py-1 text-sm font-medium text-gray-900 flex items-center gap-1 shadow-sm">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    {overallRating.toFixed(1)}
                  </div>
                )}
                
                {/* Price Badge */}
                <div className="absolute bottom-3 left-3 bg-blue-600/90 backdrop-blur-sm text-white rounded-md px-3 py-1 text-sm font-medium">
                  {formatPrice(property.rent_price, property.rent_price_currency)}
                </div>
              </div>
              
              <CardContent className="p-4">
                <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2 text-sm sm:text-base">
                  {property.generated_title || `Property in ${property.city}`}
                </h4>
                
                <div className="flex items-center gap-1 text-gray-600 text-xs sm:text-sm mb-3">
                  <MapPin className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" />
                  <span className="truncate">{property.city}, {property.state}</span>
                </div>
                
                <div className="flex items-center justify-end">
                  <Button asChild size="sm" variant="outline" className="text-blue-600 hover:text-blue-700 h-8 text-xs">
                    <Link href={reviewUrl}>
                      Read Reviews
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Pagination Dots for Mobile */}
      {limitedProperties.length > itemsPerView && (
        <div className="flex justify-center gap-2 md:hidden">
          {Array.from({ length: maxIndex + 1 }, (_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
      
      {/* View All Button */}
      <div className="text-center mt-6">
        <Button variant="outline" className="text-blue-600 hover:bg-blue-50">
          <Link href="/browse" className="flex items-center gap-2">
            <Home className="h-4 w-4" />
            View All Similar Properties
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default SimilarProperties; 